import React from 'react';
import { unreachable } from '../../../ts-utils';
import { RootBoxEffectContext } from './root-box-effect-context';
import { getViewportCenterY, findClosestBox, focusWithRetry, } from './root-box-effect-provider.utils';
import { isOffScreen } from '../../../common/utils/is-off-screen';
export const RootBoxEffectProvider = ({ effectState, children, }) => {
    const [boxRegistry] = React.useState(new Map());
    // The scroll-into-view poll below re-arms itself every 250 ms until the box is on screen. In a
    // browser that ends when the smooth scroll lands; anywhere the box can never come on screen
    // (jsdom, a box unmounted mid-scroll) it would re-arm forever. The provider owns the pending
    // handle and clears it when it unmounts, so the poll dies with the tree it was polling.
    const pendingScrollPoll = React.useRef(null);
    React.useEffect(() => () => {
        if (pendingScrollPoll.current !== null) {
            clearTimeout(pendingScrollPoll.current);
        }
    }, []);
    const closeAll = () => {
        boxRegistry.forEach((box) => box.close());
    };
    const expandClosest = (options) => {
        if (boxRegistry.size > 0) {
            const centerY = getViewportCenterY();
            const closest = findClosestBox(boxRegistry, centerY);
            if (!closest) {
                return;
            }
            const { shouldScroll, shouldFocus } = options;
            const maybeScroll = () => {
                const waitUntilOnScreen = (resolve) => {
                    if (!isOffScreen(closest.node)) {
                        pendingScrollPoll.current = null;
                        resolve(null);
                        return;
                    }
                    pendingScrollPoll.current = setTimeout(() => {
                        waitUntilOnScreen(resolve);
                    }, 250);
                };
                return new Promise((resolve) => {
                    if (!shouldScroll || !isOffScreen(closest.node)) {
                        return resolve(null);
                    }
                    closest.node.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    waitUntilOnScreen(resolve);
                });
            };
            closest
                .expand({ autoFocus: false })
                .then(() => maybeScroll())
                .then(() => shouldFocus && focusWithRetry(closest.focus));
        }
    };
    React.useEffect(() => {
        if (effectState) {
            switch (effectState.type) {
                case 'open':
                    const { shouldFocus, shouldScroll } = effectState;
                    expandClosest({ shouldFocus, shouldScroll });
                    return;
                case 'close':
                    closeAll();
                    return;
                default:
                    unreachable(effectState);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectState]);
    return (React.createElement(RootBoxEffectContext.Provider, { value: React.useMemo(() => ({
            optIn: ({ position, box }) => {
                boxRegistry.set(position, box);
            },
            optOut: (position) => {
                boxRegistry.delete(position);
            },
        }), [boxRegistry]) }, children));
};
export default RootBoxEffectProvider;
//# sourceMappingURL=root-box-effect-provider.js.map